/**
 * BoardDetail — section-grouped board canvas.
 *
 * Layout model:
 *   heading row  → static section title (not draggable)
 *   tile row(s)  → draggable within their section; each has N column slots
 *
 * UX:
 *   - No global "Add card" button — use the "Add row" bar at the bottom of
 *     each section to create a row, then click the empty slot(s) inside it.
 *   - Column switcher (1/2/3/4) is always visible per tile row.
 *   - Tile rows can be reordered by drag within their section.
 *   - Individual tiles can be reordered by drag within their row.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Text, HStack, Badge, Grid, GridItem, Spinner, Alert, AlertIcon,
  Flex, useToast, Tooltip, Button, Input, IconButton,
  Menu, MenuButton, MenuList, MenuItem, MenuDivider, Portal,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, FormControl, FormLabel, useDisclosure,
  AlertDialog, AlertDialogOverlay, AlertDialogContent,
  AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  FiMove, FiPlus, FiTrash2, FiX, FiEye, FiEdit2,
  FiMoreVertical, FiUsers, FiUser, FiShare2,
} from 'react-icons/fi';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy,
  arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { boardsApi } from './api';
import TileRender from './Tiles/Renderer';
import TileEditor from './TileEditor';
import { PRIMARY_BG, PRIMARY_BG_HOVER } from '../../theme/brand';

// ─── brand tokens ─────────────────────────────────────────────────────────────

const BRAND   = PRIMARY_BG;
const BRAND_H = PRIMARY_BG_HOVER;

const COL_MAP      = { one: 1, full: 1, two: 2, two_col: 2, three: 3, four: 4 };
const COLS_TO_TYPE = { 1: 'one', 2: 'two', 3: 'three', 4: 'four' };

// ─── layout grouping ─────────────────────────────────────────────────────────
// Groups the flat layout array into logical sections:
//   { heading, headingIdx, tileRows[], tileIndices[] }
// Each section = one optional heading row + consecutive tile rows.

function groupLayoutIntoSections(layout) {
  const sections = [];
  let cur = null;

  layout.forEach((row, i) => {
    const isH = row.kind === 'heading' || row.kind === 'paragraph';
    if (isH) {
      if (cur) sections.push(cur);
      cur = { heading: row, headingIdx: i, tileRows: [], tileIndices: [] };
    } else {
      if (!cur) cur = { heading: null, headingIdx: null, tileRows: [], tileIndices: [] };
      cur.tileRows.push(row);
      cur.tileIndices.push(i);
    }
  });

  if (cur) sections.push(cur);
  return sections;
}

// ─── section heading (static, no drag) ───────────────────────────────────────

function decodeHtml(value) {
  return String(value)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function SectionHeading({ html, editMode, onRename }) {
  const title = decodeHtml(String(html || '')).replace(/<[^>]+>/g, '').trim();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(title);

  // Keep the local draft in sync when the persisted title changes underneath us.
  useEffect(() => { setDraft(title); }, [title]);

  if (!html && !editMode) return null;

  const commit = () => {
    const next = draft.trim();
    setIsEditing(false);
    if (next && next !== title && onRename) onRename(next);
    else setDraft(title);
  };

  if (isEditing) {
    return (
      <Flex align="center" gap={3} py={1} mb={2}>
        <Box w="3px" minH="18px" bg={BRAND} borderRadius="2px" flexShrink={0} alignSelf="stretch" />
        <Input
          autoFocus
          size="sm"
          maxW="360px"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { setDraft(title); setIsEditing(false); }
          }}
          fontFamily='"Inter",sans-serif'
          fontSize='15px'
          fontWeight='700'
        />
        <Box flex={1} h="1px" bg="gray.100" />
      </Flex>
    );
  }

  return (
    <Flex align="center" gap={3} py={1} mb={2} role="group">
      <Box w="3px" minH="18px" bg={BRAND} borderRadius="2px" flexShrink={0} alignSelf="stretch" />
      <Text fontFamily='"Inter",sans-serif' fontSize='15px' fontWeight='700' color='#0f172a' m={0} lineHeight='1.4'>
        {title || 'Untitled section'}
      </Text>
      {editMode && onRename && (
        <Tooltip label="Rename section" placement="top" hasArrow openDelay={400} fontSize="xs">
          <Box
            as="button"
            type="button"
            p={1}
            borderRadius="5px"
            color="gray.300"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            _hover={{ bg: 'var(--brand-tint)', color: BRAND }}
            transition="all 0.15s"
            onClick={() => { setDraft(title); setIsEditing(true); }}
          >
            <FiEdit2 size={12} />
          </Box>
        </Tooltip>
      )}
      <Box flex={1} h="1px" bg="gray.100" />
    </Flex>
  );
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[char]));
}

const COL_LABELS = { 1: 'Full width', 2: '2 columns', 3: '3 columns', 4: '4 columns' };

function ColPickerBtn({ n, active, onClick }) {
  return (
    <Tooltip label={COL_LABELS[n]} placement="top" hasArrow openDelay={400} fontSize="xs">
      <Flex
        as="button"
        direction="column"
        align="center"
        justify="center"
        gap="3px"
        px="7px"
        py="5px"
        borderRadius="6px"
        border="1.5px solid"
        borderColor={active ? BRAND : '#e2e8f0'}
        bg={active ? BRAND : 'white'}
        color={active ? 'white' : '#94a3b8'}
        _hover={{ borderColor: active ? BRAND_H : BRAND, bg: active ? BRAND_H : 'var(--brand-tint)', color: active ? 'white' : BRAND }}
        onClick={(e) => { e.stopPropagation(); onClick(n); }}
        cursor="pointer"
        transition="all 0.12s"
        flexShrink={0}
      >
        <Box display="grid" gridTemplateColumns={`repeat(${n}, 1fr)`} gap="2px" w="18px" h="9px">
          {Array.from({ length: n }).map((_, i) => (
            <Box key={i} bg="currentColor" borderRadius="1px" />
          ))}
        </Box>
        <Text fontSize="9px" fontWeight={active ? 700 : 500} lineHeight={1} userSelect="none">{n}</Text>
      </Flex>
    </Tooltip>
  );
}

// ─── empty card slot ──────────────────────────────────────────────────────────

function EmptySlot({ onClick, onRemove }) {
  return (
    <Box position="relative" h="100%">
      {/* Remove-slot button — visible on hover */}
      {onRemove && (
        <Tooltip label="Remove this slot" placement="top" hasArrow fontSize="xs" openDelay={300}>
          <Box
            as="button"
            position="absolute"
            top="8px"
            right="8px"
            zIndex={2}
            w="20px"
            h="20px"
            borderRadius="full"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="gray.400"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            _hover={{ bg: 'red.50', borderColor: 'red.300', color: 'red.500' }}
            transition="all 0.15s"
            boxShadow="0 1px 3px rgba(0,0,0,0.08)"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >
            <FiX size={10} />
          </Box>
        </Tooltip>
      )}

      <Flex
        role="group"
        direction="column"
        align="center"
        justify="center"
        minH="100px"
        h="100%"
        border="2px dashed"
        borderColor="gray.200"
        borderRadius="12px"
        cursor="pointer"
        bg="white"
        color="gray.400"
        gap={2}
        py={6}
        onClick={onClick}
        _hover={{ borderColor: BRAND, bg: 'var(--brand-tint)', color: BRAND }}
        transition="all 0.15s"
      >
        <Flex
          align="center"
          justify="center"
          border="1.5px solid currentColor"
          borderRadius="full"
          w="24px"
          h="24px"
        >
          <FiPlus size={13} />
        </Flex>
        <Text fontSize="xs" fontWeight={500}>Add card</Text>
      </Flex>
    </Box>
  );
}

// ─── add-row bar ─────────────────────────────────────────────────────────────
// Appears at the bottom of every section; user picks column count here.

function AddRowBar({ onAdd }) {
  return (
    <Flex
      align="center"
      gap={3}
      py="8px"
      px={3}
      mt={2}
      borderRadius="10px"
      border="1.5px dashed"
      borderColor="gray.200"
      bg="gray.50"
      _hover={{ borderColor: BRAND, bg: 'var(--brand-tint)' }}
      transition="all 0.15s"
      role="group"
    >
      <FiPlus size={13} color="#94a3b8" />
      <Text fontSize="xs" color="gray.500" fontWeight={600} flexShrink={0}>Add row</Text>
      <Box w="1px" h="16px" bg="gray.200" />
      <Text fontSize="xs" color="gray.400" flexShrink={0}>Choose layout:</Text>
      <HStack spacing={1}>
        {[1, 2, 3, 4].map((n) => (
          <ColPickerBtn key={n} n={n} active={false} onClick={onAdd} />
        ))}
      </HStack>
    </Flex>
  );
}

// ─── SortableRow (tile rows only) ────────────────────────────────────────────

function SortableRow({ id, children }) {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      opacity={isDragging ? 0.45 : 1}
      role="group"
    >
      {children(setActivatorNodeRef, attributes, listeners)}
    </Box>
  );
}

function SortableSection({ id, children }) {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      opacity={isDragging ? 0.9 : 1}
      mb={7}
    >
      {children(setActivatorNodeRef, attributes, listeners)}
    </Box>
  );
}

// ─── SortableTile ─────────────────────────────────────────────────────────────

function SortableTile({ id, editMode, children }) {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id });

  return (
    <GridItem
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      opacity={isDragging ? 0.4 : 1}
      minW={0}
      position="relative"
      role="group"
    >
      {editMode && (
        <Tooltip label="Drag to reorder" placement="top" hasArrow openDelay={700} fontSize="xs">
          <Box
            ref={setActivatorNodeRef}
            position="absolute"
            top="10px"
            left="10px"
            zIndex={5}
            p="3px"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition="opacity 0.2s"
            cursor="grab"
            bg="white"
            borderRadius="5px"
            boxShadow="0 1px 4px rgba(0,0,0,0.14)"
            _active={{ cursor: 'grabbing' }}
            {...attributes}
            {...listeners}
          >
            <FiMove size={11} color="#6b7280" />
          </Box>
        </Tooltip>
      )}
      {children}
    </GridItem>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function BoardDetail({ boardId, cohorts }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [state,    setState]   = useState({ loading: true, error: null, board: null });
  const [editor,   setEditor]  = useState({ open: false, mode: 'create', tile: null });
  // Which slot the next tile creation targets: { rowIdx, slotIdx } | null
  const [addingTo, setAddingTo] = useState(null);
  // Row deletion confirm: rowIdx to delete, or null
  const [deleteRowTarget, setDeleteRowTarget] = useState(null);
  const [deleteSectionTarget, setDeleteSectionTarget] = useState(null);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const cancelDeleteRef = useRef();

  // Bug-004 / Bug-008 — board-level actions (rename / move scope / delete / share)
  const renameModal = useDisclosure();
  const shareModal  = useDisclosure();
  const deleteBoardAlert = useDisclosure();
  const [boardNameDraft, setBoardNameDraft] = useState('');
  const [boardBusy, setBoardBusy] = useState(false);
  // Bug-006 — board sharing via a real tenant-user picker (no free text, no cap).
  const [shareSelected, setShareSelected] = useState([]); // [{ id, name, email, unknown? }]
  const [userDir, setUserDir] = useState([]);              // tenant directory (/user/list)
  const [userDirState, setUserDirState] = useState('idle'); // 'idle'|'loading'|'ready'|'error'
  const [userQuery, setUserQuery] = useState('');
  const [userQueryDebounced, setUserQueryDebounced] = useState('');
  // Bug-006e — baseline (persisted) access list captured when the dialog opens, so
  // Save can be gated on an actual CHANGE rather than on "empty" (an empty list is a
  // legitimate revoke). Stored as a Set of ids for order-insensitive comparison.
  const shareBaseline = useRef(new Set());
  const cancelDeleteBoardRef = useRef();

  const load = useCallback(() => {
    if (!boardId) return;
    setState({ loading: true, error: null, board: null });
    boardsApi.get(boardId)
      .then((res) => {
        const board = res?.data || null;
        setState({ loading: false, error: null, board });
        if (board && Array.isArray(board.tiles) && board.tiles.some((t) => t.cohort_scope)) {
          boardsApi.prewarm(boardId).catch(() => {});
        }
      })
      .catch((err) => setState({ loading: false, error: err?.message || 'Failed to load board', board: null }));
  }, [boardId]);

  useEffect(() => { load(); }, [load]);

  const openEdit    = (t) => setEditor({ open: true, mode: 'edit', tile: t });
  const closeEditor = () => {
    setEditor({ open: false, mode: 'create', tile: null });
    setAddingTo(null);
  };

  // Optimistic layout update then persist
  const saveLayout = useCallback(async (newLayout) => {
    setState((prev) => ({ ...prev, board: { ...prev.board, layout: newLayout } }));
    try {
      await boardsApi.updateBoard(boardId, { layout: newLayout });
    } catch (e) {
      // Surface the REAL backend reason (403 permission / 400 validation / 500),
      // instead of a generic "Failed to save" that hides why.
      const status = e?.response?.status;
      const reason = e?.response?.data?.error?.message || e?.message;
      console.error('[boards] saveLayout failed:', status, e?.response?.data || e?.message);
      toast({
        status: 'error',
        title: 'Failed to save layout',
        description: status === 403
          ? "You don't have edit permission on this board (owner / editor only)."
          : `${status || ''} ${reason || ''}`.trim(),
        duration: 6000,
        isClosable: true,
      });
      load();
    }
  }, [boardId, toast, load]);

  // ── Bug-004 / Bug-008 — board-level mutations ──

  const openRename = () => {
    setBoardNameDraft(state.board?.name || '');
    renameModal.onOpen();
  };

  const saveRename = async () => {
    const name = boardNameDraft.trim();
    if (!name) {
      toast({ status: 'warning', title: 'Board name is required', duration: 3000 });
      return;
    }
    setBoardBusy(true);
    try {
      await boardsApi.updateBoard(boardId, { name });
      toast({ status: 'success', title: 'Board renamed', duration: 2000 });
      renameModal.onClose();
      load();
    } catch (e) {
      toast({ status: 'error', title: 'Rename failed', description: e?.response?.data?.error?.message || e.message });
    } finally {
      setBoardBusy(false);
    }
  };

  const moveScope = async (scope) => {
    setBoardBusy(true);
    try {
      await boardsApi.updateBoard(boardId, { scope });
      toast({ status: 'success', title: scope === 'team' ? 'Moved to Team' : 'Moved to Personal', duration: 2000 });
      load();
    } catch (e) {
      toast({ status: 'error', title: 'Move failed', description: e?.response?.data?.error?.message || e.message });
    } finally {
      setBoardBusy(false);
    }
  };

  const confirmDeleteBoard = async () => {
    setBoardBusy(true);
    try {
      await boardsApi.deleteBoard(boardId);
      toast({ status: 'success', title: 'Board deleted', duration: 2000 });
      deleteBoardAlert.onClose();
      navigate('/insights');
    } catch (e) {
      toast({ status: 'error', title: 'Delete failed', description: e?.response?.data?.error?.message || e.message });
      setBoardBusy(false);
    }
  };

  // Debounce the picker query so client-side filtering doesn't thrash per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setUserQueryDebounced(userQuery.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [userQuery]);

  const openShare = () => {
    setUserQuery('');
    setUserQueryDebounced('');
    // Bug-006d: seed the chips from the backend-RESOLVED access list (shared_with),
    // so existing recipients show their real name/email on open instead of a
    // "User <id>" placeholder. Deleted users arrive flagged `unknown` → a distinct,
    // removable chip. Fall back to raw editor_user_ids only if an older API response
    // didn't include shared_with.
    const resolved = Array.isArray(state.board?.shared_with) ? state.board.shared_with : null;
    const seed = resolved
      ? resolved.map((u) => ({ id: String(u.id), name: u.name || null, email: u.email || '', unknown: !!u.unknown }))
      : (state.board?.editor_user_ids || []).map((id) => ({ id: String(id), name: null, email: '', unknown: false }));
    setShareSelected(seed);
    // Bug-006e: baseline = the ids currently persisted, for the dirty check.
    shareBaseline.current = new Set(seed.map((s) => s.id));
    shareModal.onOpen();
    setUserDirState('loading');
    boardsApi.listUsers()
      .then((r) => {
        const dir = r.data || [];
        setUserDir(dir);
        setUserDirState(r.error ? 'error' : 'ready');
        // Resolve any still-unnamed chips from the directory (belt-and-braces if
        // shared_with was absent). Never overwrites a name we already have.
        setShareSelected((prev) => prev.map((s) => {
          if (s.name) return s;
          const u = dir.find((d) => d.id === s.id);
          return u ? { id: u.id, name: u.name, email: u.email, unknown: false } : s;
        }));
      })
      .catch(() => setUserDirState('error'));
  };

  // Bug-006e: dirty = the working selection differs from the persisted baseline as
  // a SET (order-insensitive, ids string-normalised). An empty selection is dirty
  // when the board previously had shares (a revoke) and clean when it had none.
  const shareDirty = (() => {
    const cur = new Set(shareSelected.map((s) => String(s.id)));
    const base = shareBaseline.current;
    if (cur.size !== base.size) return true;
    for (const id of cur) if (!base.has(id)) return true;
    return false;
  })();

  const addShareUser = (u) => {
    setShareSelected((p) => (p.some((s) => s.id === u.id) ? p : [...p, u]));
    setUserQuery('');
  };
  const removeShareUser = (id) => setShareSelected((p) => p.filter((s) => s.id !== id));

  const saveShare = async () => {
    const ids = shareSelected.map((s) => s.id);
    setBoardBusy(true);
    try {
      await boardsApi.updateBoard(boardId, { editor_user_ids: ids });
      // Bug-006e: the write succeeded — the new list IS the persisted baseline now,
      // so Save returns to disabled without needing the dialog reopened.
      shareBaseline.current = new Set(ids.map((x) => String(x)));
      toast({ status: 'success', title: ids.length ? 'Board shared' : 'Sharing cleared', duration: 2000 });
      shareModal.onClose();
      load();
    } catch (e) {
      const code = e?.response?.data?.error?.code;
      toast({
        status: 'error',
        title: 'Share failed',
        description: code === 'unknown_editors'
          ? 'Some selected users could not be validated — please re-pick them from the list.'
          : (e?.response?.data?.error?.message || e.message),
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBoardBusy(false);
    }
  };

  // Directory matches for the picker: exclude already-selected, filter by the
  // debounced query (name / email / username / id), cap the visible list.
  const shareSelectedIds = new Set(shareSelected.map((s) => s.id));
  const shareCandidates = userDir
    .filter((u) => !shareSelectedIds.has(u.id))
    .filter((u) => {
      const q = userQueryDebounced;
      if (!q) return true;
      return u.name.toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.username || '').toLowerCase().includes(q)
        || u.id.includes(q);
    })
    .slice(0, 25);

  // Bug-005 — rename a section heading (persist its content_html in board.layout)
  const renameSection = (headingIdx, title) => {
    if (headingIdx === null || headingIdx === undefined) return;
    const newLayout = (state.board?.layout || []).map((row, i) =>
      i === headingIdx ? { ...row, content_html: escapeHtml(title) } : row
    );
    saveLayout(newLayout);
    toast({ status: 'success', title: 'Section renamed', duration: 2000 });
  };

  // User clicks an empty slot → remember target, open editor
  const onSlotAddCard = (rowIdx, slotIdx) => {
    setAddingTo({ rowIdx, slotIdx });
    setEditor({ open: true, mode: 'create', tile: null });
  };

  const onTileSaved = async (newTile) => {
    if (editor.mode === 'create' && state.board && newTile) {
      let newLayout;
      if (addingTo !== null) {
        const { rowIdx } = addingTo;
        newLayout = (state.board.layout || []).map((row, i) =>
          i === rowIdx
            ? { ...row, tile_ids: [...(row.tile_ids || []), String(newTile._id)] }
            : row
        );
      } else {
        // Fallback: create a fresh 1-col row at the end
        newLayout = [
          ...(state.board.layout || []),
          { row_type: 'one', kind: 'tile', tile_ids: [String(newTile._id)], content_html: '' },
        ];
      }
      try {
        await boardsApi.updateBoard(boardId, { layout: newLayout });
      } catch (e) {
        toast({ status: 'warning', title: 'Tile created but layout save failed', description: e.message });
      }
      setAddingTo(null);
    }
    load();
  };

  const onTileDuplicate = async (tile) => {
    const payload = {
      type: tile.type, title: `${tile.title} (copy)`, subtitle: tile.subtitle || '',
      source: tile.source, cohort_scope: tile.cohort_scope || null,
      viz_config: { ...(tile.viz_config || {}) }, refresh_frequency: tile.refresh_frequency || 'daily',
    };
    try {
      const r       = await boardsApi.createTile(boardId, payload);
      const newTile = r?.data;
      if (newTile && state.board) {
        const newLayout = [
          ...(state.board.layout || []),
          { row_type: 'one', kind: 'tile', tile_ids: [String(newTile._id)], content_html: '' },
        ];
        await boardsApi.updateBoard(boardId, { layout: newLayout });
      }
      // Bug-012: duplication used to give no feedback. Confirm + point the user to the new copy.
      toast({ status: 'success', title: 'Tile duplicated', description: `"${payload.title}" added to the bottom of the board`, duration: 2500 });
      await load();
      // Scroll the freshly-added copy into view so the action is visible.
      if (newTile?._id) {
        setTimeout(() => {
          const el = document.getElementById(`tile-${newTile._id}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    } catch (e) {
      toast({ status: 'error', title: 'Duplicate failed', description: e?.response?.data?.error?.message || e.message });
    }
  };

  const onTileDelete = async (tile) => {
    await boardsApi.deleteTile(boardId, tile._id || tile.id);
    if (state.board) {
      const tid = String(tile._id || tile.id);
      const newLayout = (state.board.layout || [])
        .map((row) => ({ ...row, tile_ids: (row.tile_ids || []).filter((x) => String(x) !== tid) }))
        .filter((row) => row.kind !== 'tile' || (row.tile_ids && row.tile_ids.length > 0));
      await boardsApi.updateBoard(boardId, { layout: newLayout });
    }
    load();
  };

  const sectionSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const rowSensors  = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const tileSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleSectionDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const sectionIds = sections.map((_, idx) => `section-${idx}`);
    const oldIdx = sectionIds.indexOf(String(active.id));
    const newIdx = sectionIds.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;

    const sectionRanges = sections.map((section) => {
      const start = section.headingIdx !== null ? section.headingIdx : section.tileIndices[0];
      const end = section.tileIndices.length > 0
        ? section.tileIndices[section.tileIndices.length - 1]
        : section.headingIdx;
      return { start, end };
    });

    const reordered = arrayMove(sectionRanges, oldIdx, newIdx);
    const newLayout = reordered.flatMap((range) => layout.slice(range.start, range.end + 1));
    saveLayout(newLayout);
  };

  if (state.loading) return <Flex justify="center" align="center" h="60vh"><Spinner color={BRAND} /></Flex>;
  if (state.error)   return <Alert status="error" m={4}><AlertIcon />{state.error}</Alert>;
  if (!state.board)  return <Alert status="info"  m={4}><AlertIcon />Board not found.</Alert>;

  const board      = state.board;
  // Bug-006b/006c: board-structure actions (Share / Rename / Move / Delete) are
  // owner-only. Non-owners with access keep view + tile-edit only. The server is
  // the enforcement point; this gate only decides whether to OFFER the controls.
  //
  // Ownership comes from the backend `is_owner` flag (authoritative — the server
  // knows the caller's id for certain). The client-derived check is a fallback for
  // an older API response; it works now that login persists the per-user id, but
  // must never be the sole source (that was the 006c outage — currentUserId was
  // '' so the whole menu silently vanished for every owner).
  const currentUserId = (() => {
    try { const u = JSON.parse(localStorage.getItem('user') || 'null'); return String(u?.id || u?.username || ''); }
    catch { return ''; }
  })();
  const clientIsOwner = currentUserId !== '' && String(board.owner_id) === currentUserId;
  const isOwner = typeof board.is_owner === 'boolean' ? board.is_owner : clientIsOwner;
  // Safe-failure telemetry: if neither source can resolve ownership, log it rather
  // than silently hiding the menu (006c root-cause was an invisible false).
  if (board && board.is_owner === undefined && currentUserId === '') {
    // eslint-disable-next-line no-console
    console.warn('[BoardDetail] ownership unresolved: backend is_owner missing and no client user id; actions menu hidden.');
  }
  const layout     = board.layout || [];
  const tileById   = new Map((board.tiles || []).map((t) => [String(t._id), t]));
  const cohortById = new Map((Array.isArray(cohorts) ? cohorts : []).map((c) => [String(c._id), c]));
  const cohortFor  = (t) => t?.cohort_scope ? cohortById.get(String(t.cohort_scope)) : null;

  // Tiles that exist but aren't referenced by any row
  const inLayout = new Set();
  layout.forEach((r) => (r.tile_ids || []).forEach((t) => inLayout.add(String(t))));
  const stray = (board.tiles || []).filter((t) => !inLayout.has(String(t._id)));

  const sections = groupLayoutIntoSections(layout);

  // ── drag + layout handlers ──

  // Reorder tile ROWS within one section (in-place swap)
  const handleSectionRowDragEnd = (section) => ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const rowIds = section.tileIndices.map((i) => `row-${i}`);
    const oldIdx = rowIds.indexOf(String(active.id));
    const newIdx = rowIds.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;

    const sectionRows = section.tileIndices.map((i) => layout[i]);
    const reordered   = arrayMove(sectionRows, oldIdx, newIdx);
    const newLayout   = [...layout];
    section.tileIndices.forEach((layoutIdx, i) => { newLayout[layoutIdx] = reordered[i]; });
    saveLayout(newLayout);
  };

  // Reorder TILES within one row
  const handleTileDragEnd = (rowIdx) => ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids    = (layout[rowIdx].tile_ids || []).map(String);
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;
    saveLayout(layout.map((r, i) =>
      i === rowIdx ? { ...r, tile_ids: arrayMove(ids, oldIdx, newIdx) } : r
    ));
  };

  // Change column count of a row (guard: can't shrink below tile count)
  const handleColChange = (rowIdx, cols) => {
    const count = (layout[rowIdx].tile_ids || []).length;
    if (cols < count) {
      toast({
        status: 'warning',
        title: `Remove ${count - cols} card${count - cols > 1 ? 's' : ''} first`,
        description: `This row has ${count} cards. Switch to ${cols} column${cols > 1 ? 's' : ''} after removing the extras.`,
        duration: 4000,
      });
      return;
    }
    saveLayout(layout.map((r, i) =>
      i === rowIdx ? { ...r, row_type: COLS_TO_TYPE[cols] } : r
    ));
  };

  // Insert an empty row at the end of a section (before the next heading)
  const handleAddRow = (section, cols) => {
    const insertAt = section.tileIndices.length > 0
      ? section.tileIndices[section.tileIndices.length - 1] + 1
      : section.headingIdx !== null
        ? section.headingIdx + 1
        : layout.length;

    const newRow    = { row_type: COLS_TO_TYPE[cols], kind: 'tile', tile_ids: [], content_html: '' };
    const newLayout = [...layout.slice(0, insertAt), newRow, ...layout.slice(insertAt)];
    saveLayout(newLayout);
  };

  const handleAddSection = () => {
    const title = newSectionTitle.trim();
    if (!title) {
      toast({ status: 'warning', title: 'Enter a section name first', duration: 3000 });
      return;
    }
    const headingRow = { kind: 'heading', content_html: title };
    const sectionRow = { row_type: 'one', kind: 'tile', tile_ids: [], content_html: '' };
    saveLayout([...(layout || []), headingRow, sectionRow]);
    setNewSectionTitle('');
  };

  const confirmDeleteRow = async () => {
    if (deleteRowTarget === null) return;
    const row = layout[deleteRowTarget];
    const tileIdsToDelete = (row?.tile_ids || []).map(String);
    const section = sections.find((section) => section.tileIndices.includes(deleteRowTarget));
    const removeIndexes = new Set([deleteRowTarget]);
    if (section && section.headingIdx !== null && section.tileRows.length === 1) {
      removeIndexes.add(section.headingIdx);
    }

    const newLayout = layout.filter((_, i) => !removeIndexes.has(i));
    setDeleteRowTarget(null);
    await saveLayout(newLayout);

    await Promise.allSettled(
      tileIdsToDelete.map((tid) => boardsApi.deleteTile(boardId, tid))
    );
    load();
  };

  const confirmDeleteSection = async () => {
    if (deleteSectionTarget === null) return;
    const section = sections[deleteSectionTarget];
    if (!section) return;
    const removeIndexes = new Set(section.tileIndices);
    if (section.headingIdx !== null) removeIndexes.add(section.headingIdx);
    const tileIdsToDelete = section.tileRows.flatMap((row) => (row.tile_ids || []).map(String));
    const newLayout = layout.filter((_, i) => !removeIndexes.has(i));

    setDeleteSectionTarget(null);
    await saveLayout(newLayout);
    await Promise.allSettled(
      tileIdsToDelete.map((tid) => boardsApi.deleteTile(boardId, tid))
    );
    load();
  };

  return (
    <Box w="100%" px={{ base: 4, md: 6, xl: 10, '2xl': 14 }} py={6}>
      {/* ── Board header ── */}
      <Flex mb={2} align="center" justify="space-between" gap={4} flexWrap="wrap">
        <HStack spacing={3} align="center">
          <Text fontSize="3xl">{board.emoji || '📊'}</Text>
          <Text fontSize="2xl" fontWeight={700} color="#0f172a" fontFamily='"Inter",sans-serif'>
            {board.name}
          </Text>
          <Badge
            bg={board.scope === 'team' ? BRAND : '#7c3aed'}
            color="white"
            fontSize="xs"
            px={2}
            py={0.5}
            borderRadius="full"
            fontWeight={600}
            textTransform="capitalize"
          >
            {board.scope}
          </Badge>
          {/* Bug-006 — Shared pill. Bug-006b: only the OWNER can open the
              manageable access list; non-owners see it as an informational badge.
              Bug-006a: per-user sharing is meaningless on TEAM boards (visible
              team-wide already), so the pill only shows on personal boards. */}
          {board.scope === 'personal' && Array.isArray(board.editor_user_ids) && board.editor_user_ids.length > 0 && (
            <Badge
              as={isOwner ? 'button' : undefined}
              onClick={isOwner ? openShare : undefined}
              cursor={isOwner ? 'pointer' : 'default'}
              bg="#ecfdf5"
              color="#047857"
              fontSize="xs"
              px={2}
              py={0.5}
              borderRadius="full"
              fontWeight={600}
              display="inline-flex"
              alignItems="center"
              gap={1}
              _hover={isOwner ? { bg: '#d1fae5' } : undefined}
              title={isOwner ? 'Manage who has access' : 'Shared with your team'}
            >
              <FiUsers size={11} /> Shared · {board.editor_user_ids.length}
            </Badge>
          )}
        </HStack>

        <HStack spacing={2} flexShrink={0}>
        {/* View / Edit mode toggle */}
        <HStack
          spacing={0}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="7px"
          overflow="hidden"
          bg="white"
          boxShadow="0 1px 2px rgba(0,0,0,0.05)"
          flexShrink={0}
        >
          <Button
            size="xs"
            variant="unstyled"
            display="flex"
            alignItems="center"
            px={3}
            h="26px"
            borderRadius={0}
            bg={!isEditMode ? BRAND : 'transparent'}
            color={!isEditMode ? 'white' : 'gray.400'}
            fontSize="11px"
            fontWeight={600}
            _hover={{ bg: !isEditMode ? BRAND_H : 'gray.50', color: !isEditMode ? 'white' : 'gray.600' }}
            transition="all 0.15s"
            onClick={() => setIsEditMode(false)}
          >
            <FiEye size={11} />
            <Text as="span" ml={1}>View</Text>
          </Button>
          <Box w="1px" h="26px" bg="gray.200" flexShrink={0} />
          <Button
            size="xs"
            variant="unstyled"
            display="flex"
            alignItems="center"
            px={3}
            h="26px"
            borderRadius={0}
            bg={isEditMode ? BRAND : 'transparent'}
            color={isEditMode ? 'white' : 'gray.400'}
            fontSize="11px"
            fontWeight={600}
            _hover={{ bg: isEditMode ? BRAND_H : 'gray.50', color: isEditMode ? 'white' : 'gray.600' }}
            transition="all 0.15s"
            onClick={() => setIsEditMode(true)}
          >
            <FiEdit2 size={11} />
            <Text as="span" ml={1}>Edit</Text>
          </Button>
        </HStack>

        {/* Bug-006b: board-structure actions (Rename / Move / Share / Delete)
            are OWNER-ONLY. Hide the whole menu from non-owners — they keep view
            + tile-edit. The server-side ownership guard is the real enforcement;
            this just stops offering actions the API will 403. */}
        {isOwner && (
        <Menu placement="bottom-end" isLazy strategy="fixed">
          <MenuButton
            as={IconButton}
            icon={<FiMoreVertical />}
            size="sm"
            variant="ghost"
            aria-label="Board actions"
          />
          <Portal>
            <MenuList fontSize="sm" minW="200px" zIndex={9999}>
              <MenuItem icon={<FiEdit2 />} onClick={openRename}>Rename board</MenuItem>
              {board.scope === 'team' ? (
                <MenuItem icon={<FiUser />} onClick={() => moveScope('personal')} isDisabled={boardBusy}>
                  Move to Personal
                </MenuItem>
              ) : (
                <MenuItem icon={<FiUsers />} onClick={() => moveScope('team')} isDisabled={boardBusy}>
                  Move to Team
                </MenuItem>
              )}
              {/* Bug-006a: Share is meaningless on Team boards (already visible
                  team-wide) — only offer it on Personal boards. */}
              {board.scope === 'personal' && (
                <MenuItem icon={<FiShare2 />} onClick={openShare}>Share…</MenuItem>
              )}
              <MenuDivider />
              <MenuItem icon={<FiTrash2 />} color="red.600" onClick={deleteBoardAlert.onOpen}>
                Delete board
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
        )}
        </HStack>
      </Flex>

      {board.description && (
        <Text color="#475569" mb={5} fontSize="sm"  lineHeight={1.6}>
          {board.description}
        </Text>
      )}

      {isEditMode && (
        <Box mb={6} maxW="3xl">
          <Text fontSize="sm" color="gray.600" mb={2}>Create a new section with a title, then add rows inside it.</Text>
          <HStack spacing={2} flexWrap="wrap">
            <Input
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              placeholder="Section name"
              size="sm"
              maxW="320px"
            />
            <Button size="sm" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} _active={{ bg: 'var(--brand-primary-active)' }} onClick={handleAddSection} isDisabled={!newSectionTitle.trim()}>
              Add section
            </Button>
          </HStack>
        </Box>
      )}

      {/* ── Sections ── */}
      {sections.length === 0 ? (
        <Box py={12} textAlign="center">
          <Text color="gray.400" fontSize="sm" mb={4}>
            {isEditMode
              ? 'This board has no sections yet. Create a section first, then add rows inside it.'
              : 'This board is empty. Switch to Edit mode to add sections and cards.'}
          </Text>
          {!isEditMode && (
            <Button size="sm" bg={BRAND} color="white" _hover={{ bg: BRAND_H }} onClick={() => setIsEditMode(true)}>
              Switch to Edit mode
            </Button>
          )}
        </Box>
      ) : (
        <DndContext
          sensors={sectionSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSectionDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={sections.map((_, idx) => `section-${idx}`)} strategy={verticalListSortingStrategy}>
            {sections.map((section, sIdx) => {
              const sectionRowIds = section.tileIndices.map((i) => `row-${i}`);

              return (
                <SortableSection key={`section-${sIdx}`} id={`section-${sIdx}`}>
                  {(setActivatorRef, activatorAttrs, activatorListeners) => (
                    <Box>
                      {/* Hidden ref anchor ensures dnd-kit activator is always mounted */}
                      {!isEditMode && <Box ref={setActivatorRef} display="none" aria-hidden />}
                      {isEditMode && (
                        <Flex align="center" justify="space-between" mb={2}>
                          <Tooltip label="Drag section" placement="left" hasArrow openDelay={500} fontSize="xs">
                            <Box
                              ref={setActivatorRef}
                              p={1}
                              cursor="grab"
                              color="gray.300"
                              _hover={{ color: 'gray.500', bg: 'gray.100' }}
                              _active={{ cursor: 'grabbing' }}
                              borderRadius="5px"
                              transition="all 0.15s"
                              {...activatorAttrs}
                              {...activatorListeners}
                            >
                              <FiMove size={14} />
                            </Box>
                          </Tooltip>
                          <Tooltip label="Delete section" placement="top" hasArrow openDelay={300} fontSize="xs">
                            <Box
                              as="button"
                              type="button"
                              p={1}
                              borderRadius="5px"
                              color="gray.300"
                              _hover={{ bg: 'red.50', color: 'red.500' }}
                              transition="all 0.15s"
                              onClick={() => setDeleteSectionTarget(sIdx)}
                            >
                              <FiTrash2 size={14} />
                            </Box>
                          </Tooltip>
                        </Flex>
                      )}

                      {/* Section heading — draggable by the handle above; inline-renamable in edit mode */}
                      {section.heading && (
                        <SectionHeading
                          html={section.heading.content_html}
                          editMode={isEditMode}
                          onRename={(title) => renameSection(section.headingIdx, title)}
                        />
                      )}

            {/* Tile rows — draggable within this section only */}
            {section.tileRows.length > 0 && (
              <DndContext
                sensors={rowSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionRowDragEnd(section)}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext items={sectionRowIds} strategy={verticalListSortingStrategy}>
                  {section.tileRows.map((row, rIdx) => {
                    const rowIdx     = section.tileIndices[rIdx];
                    const cols       = COL_MAP[row.row_type] || 1;
                    const tileList   = (row.tile_ids || []).map((tid) => tileById.get(String(tid))).filter(Boolean);
                    const tileIds    = tileList.map((t) => String(t._id));
                    const emptyCount = Math.max(0, cols - tileList.length);

                    return (
                      <SortableRow key={`row-${rowIdx}`} id={`row-${rowIdx}`}>
                        {(setActivatorRef, activatorAttrs, activatorListeners) => (
                          <Box mb={4}>
                            {/* Row toolbar — edit mode only */}
                            {!isEditMode && <Box ref={setActivatorRef} display="none" aria-hidden />}
                            {isEditMode && (
                              <Flex align="center" justify="space-between" h="28px" mb={2} px={0.5}>
                                <Tooltip label="Drag to reorder this row" placement="left" hasArrow openDelay={600} fontSize="xs">
                                  <Box
                                    ref={setActivatorRef}
                                    p={1}
                                    cursor="grab"
                                    color="gray.300"
                                    _groupHover={{ color: 'gray.500' }}
                                    _hover={{ bg: 'gray.100', color: 'gray.600' }}
                                    _active={{ cursor: 'grabbing' }}
                                    borderRadius="4px"
                                    transition="all 0.15s"
                                    {...activatorAttrs}
                                    {...activatorListeners}
                                  >
                                    <FiMove size={13} />
                                  </Box>
                                </Tooltip>

                                <HStack spacing={2} align="center">
                                  <HStack spacing={1} align="center">
                                    <Text fontSize="10px" color="gray.400" fontWeight={500} mr={1} userSelect="none">
                                      Layout
                                    </Text>
                                    {[1, 2, 3, 4].map((n) => (
                                      <ColPickerBtn
                                        key={n}
                                        n={n}
                                        active={cols === n}
                                        onClick={(n) => handleColChange(rowIdx, n)}
                                      />
                                    ))}
                                  </HStack>
                                  <Box w="1px" h="16px" bg="gray.200" />
                                  <Tooltip label="Delete row" placement="top" hasArrow openDelay={400} fontSize="xs">
                                    <Box
                                      as="button"
                                      p={1}
                                      borderRadius="5px"
                                      color="gray.300"
                                      _hover={{ bg: 'red.50', color: 'red.500' }}
                                      transition="all 0.15s"
                                      onClick={() => setDeleteRowTarget(rowIdx)}
                                    >
                                      <FiTrash2 size={13} />
                                    </Box>
                                  </Tooltip>
                                </HStack>
                              </Flex>
                            )}

                            {/* Tiles + empty slots in a shared grid */}
                            <DndContext
                              sensors={tileSensors}
                              collisionDetection={closestCenter}
                              onDragEnd={handleTileDragEnd(rowIdx)}
                            >
                              <SortableContext items={tileIds} strategy={horizontalListSortingStrategy}>
                                <Grid templateColumns={`repeat(${cols}, minmax(0, 1fr))`} gap={4}>
                                  {/* Occupied slots */}
                                  {tileList.map((t) => (
                                    <SortableTile key={String(t._id)} id={String(t._id)} editMode={isEditMode}>
                                      <TileRender
                                        boardId={boardId}
                                        tile={t}
                                        cohort={cohortFor(t)}
                                        editMode={isEditMode}
                                        onEdit={openEdit}
                                        onDuplicate={onTileDuplicate}
                                        onDelete={onTileDelete}
                                      />
                                    </SortableTile>
                                  ))}
                                  {/* Empty slots — edit mode only */}
                                  {isEditMode && Array.from({ length: emptyCount }).map((_, ei) => (
                                    <GridItem key={`empty-${ei}`} minW={0}>
                                      <EmptySlot
                                        onClick={() => onSlotAddCard(rowIdx, tileList.length + ei)}
                                        onRemove={() => handleColChange(rowIdx, cols - 1)}
                                      />
                                    </GridItem>
                                  ))}
                                </Grid>
                              </SortableContext>
                            </DndContext>
                          </Box>
                        )}
                      </SortableRow>
                    );
                  })}
                </SortableContext>
              </DndContext>
            )}

            {/* Add row bar — edit mode only */}
            {isEditMode && <AddRowBar onAdd={(cols) => handleAddRow(section, cols)} />}
          </Box>
                )}
              </SortableSection>
            );
          })}
          </SortableContext>
        </DndContext>
      )}

      {/* Stray tiles (exist as objects but not in any layout row) */}
      {stray.length > 0 && (
        <Box mt={6} pt={4} borderTop="1px dashed" borderColor="gray.200">
          <Text fontSize="xs" color="gray.500" mb={3}>📥 Unplaced cards</Text>
          <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap={4}>
            {stray.map((t) => (
              <GridItem key={String(t._id)} minW={0}>
                <TileRender
                  boardId={boardId} tile={t} cohort={cohortFor(t)}
                  editMode={isEditMode}
                  onEdit={openEdit} onDuplicate={onTileDuplicate} onDelete={onTileDelete}
                />
              </GridItem>
            ))}
          </Grid>
        </Box>
      )}

      {/* Delete row confirmation */}
      <AlertDialog
        isOpen={deleteRowTarget !== null}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setDeleteRowTarget(null)}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="md" fontWeight={700}>Delete row?</AlertDialogHeader>
          <AlertDialogBody fontSize="sm" color="gray.600">
            {deleteRowTarget !== null && (layout[deleteRowTarget]?.tile_ids || []).length > 0
              ? `This row contains ${(layout[deleteRowTarget]?.tile_ids || []).length} card${(layout[deleteRowTarget]?.tile_ids || []).length > 1 ? 's' : ''}. Deleting the row will also permanently delete those cards.`
              : 'This empty row will be removed. If it is the only row in the section, the section title will be removed too.'}
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button ref={cancelDeleteRef} size="sm" variant="ghost" onClick={() => setDeleteRowTarget(null)}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="red" onClick={confirmDeleteRow}>
              Delete row
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        isOpen={deleteSectionTarget !== null}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setDeleteSectionTarget(null)}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="md" fontWeight={700}>Delete section?</AlertDialogHeader>
          <AlertDialogBody fontSize="sm" color="gray.600">
            {(() => {
              const section = deleteSectionTarget !== null ? sections[deleteSectionTarget] : null;
              const cardCount = section ? section.tileRows.reduce((sum, row) => sum + (row.tile_ids || []).length, 0) : 0;
              return section
                ? cardCount > 0
                  ? `This section contains ${section.tileRows.length} row${section.tileRows.length > 1 ? 's' : ''} and ${cardCount} card${cardCount > 1 ? 's' : ''}. Deleting the section will remove all rows and cards.`
                  : 'This section has no rows and will be removed.'
                : 'Delete this section?';
            })()}
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button ref={cancelDeleteRef} size="sm" variant="ghost" onClick={() => setDeleteSectionTarget(null)}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="red" onClick={confirmDeleteSection}>
              Delete section
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bug-004 — Rename board modal */}
      <Modal isOpen={renameModal.isOpen} onClose={renameModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Rename board</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm">Board name</FormLabel>
              <Input
                autoFocus
                value={boardNameDraft}
                onChange={(e) => setBoardNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); }}
                placeholder="Board name"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={renameModal.onClose}>Cancel</Button>
            <Button
              size="sm"
              bg={BRAND}
              color="white"
              _hover={{ bg: BRAND_H }}
              onClick={saveRename}
              isLoading={boardBusy}
              isDisabled={!boardNameDraft.trim()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bug-008 — Share board modal */}
      <Modal isOpen={shareModal.isOpen} onClose={shareModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Share board</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm">People with access</FormLabel>

              {/* Owner (always, non-removable) + selected recipients as removable chips */}
              <HStack spacing={2} wrap="wrap" mb={3}>
                <HStack spacing={1} bg="gray.100" borderRadius="full" px={2} py={1}>
                  <FiUser size={12} />
                  <Text fontSize="xs" fontWeight={600}>
                    {(userDir.find((u) => u.id === String(board.owner_id))?.name) || 'Owner'} · owner
                  </Text>
                </HStack>
                {shareSelected.map((s) => {
                  // Bug-006d: a chip whose id no longer resolves to a real user
                  // (deleted, or a bad value that reached the DB by some other path)
                  // renders as a DISTINCT "Unknown user" chip the owner can remove —
                  // never a plausible-looking "User <id>" that reads like a real name.
                  const unknown = s.unknown || !s.name;
                  const label = unknown ? `Unknown user (${s.id})` : s.name;
                  return (
                    <HStack
                      key={s.id} spacing={1} borderRadius="full" pl={2} pr={1} py={1}
                      bg={unknown ? '#fef2f2' : '#e0e7ff'} color={unknown ? '#b91c1c' : '#3730a3'}
                    >
                      <Text fontSize="xs" fontWeight={600}>{label}</Text>
                      <IconButton
                        aria-label={`Remove ${label}`} icon={<FiX />} size="2xs" variant="ghost"
                        color={unknown ? '#b91c1c' : '#3730a3'} minW={4} h={4} onClick={() => removeShareUser(s.id)}
                      />
                    </HStack>
                  );
                })}
              </HStack>

              {/* Searchable tenant-directory picker */}
              <Input
                autoFocus
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Search people by name or email…"
              />
              {userQuery.trim() && (
                <Box mt={1} maxH="220px" overflowY="auto" borderWidth="1px" borderColor="gray.200" borderRadius="md">
                  {userDirState === 'loading' && (
                    <HStack p={3} color="gray.500"><Spinner size="xs" /><Text fontSize="sm">Loading people…</Text></HStack>
                  )}
                  {userDirState === 'error' && (
                    <Text p={3} fontSize="sm" color="orange.500">
                      Couldn't load the user directory (need the user-list permission).
                    </Text>
                  )}
                  {userDirState === 'ready' && shareCandidates.length === 0 && (
                    <Text p={3} fontSize="sm" color="gray.500">No users found matching “{userQuery.trim()}”.</Text>
                  )}
                  {userDirState === 'ready' && shareCandidates.map((u) => (
                    <HStack
                      key={u.id} as="button" type="button" w="100%" justify="space-between"
                      px={3} py={2} _hover={{ bg: 'gray.50' }} onClick={() => addShareUser(u)} textAlign="left"
                    >
                      <Box textAlign="left">
                        <Text fontSize="sm" fontWeight={600}>{u.name}</Text>
                        {u.email ? <Text fontSize="xs" color="gray.500">{u.email}</Text> : null}
                      </Box>
                      <FiPlus />
                    </HStack>
                  ))}
                </Box>
              )}
              <Text fontSize="xs" color="gray.500" mt={2}>
                Shared users can view and edit this board. No limit on how many you add.
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={shareModal.onClose}>Cancel</Button>
            {/* Bug-006e: Save is enabled only when the selection differs from the
                persisted baseline (a set-compare) — NOT gated on "empty", so an
                owner can still commit removing the last recipient (a revoke). Also
                disabled while a save is in flight to prevent duplicate writes. */}
            <Button
              size="sm"
              bg={BRAND}
              color="white"
              _hover={{ bg: BRAND_H }}
              onClick={saveShare}
              isLoading={boardBusy}
              isDisabled={!shareDirty || boardBusy}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bug-004 — Delete board confirmation */}
      <AlertDialog
        isOpen={deleteBoardAlert.isOpen}
        leastDestructiveRef={cancelDeleteBoardRef}
        onClose={deleteBoardAlert.onClose}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="md" fontWeight={700}>Delete board?</AlertDialogHeader>
          <AlertDialogBody fontSize="sm" color="gray.600">
            "{board.name}" and all of its sections and cards will be removed. This can't be undone.
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button ref={cancelDeleteBoardRef} size="sm" variant="ghost" onClick={deleteBoardAlert.onClose}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="red" onClick={confirmDeleteBoard} isLoading={boardBusy}>
              Delete board
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tile editor drawer */}
      <TileEditor
        isOpen={editor.open} onClose={closeEditor} mode={editor.mode}
        boardId={boardId} tile={editor.tile} cohorts={cohorts} onSaved={onTileSaved}
      />
    </Box>
  );
}
